import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';
import { ContainerQueryFallback } from '@/shared/constant/ContainerQueryFallback';
import { isContainerQueryFallback as isSSRContainerQueryFallback } from '@/shared/util/ssr';
import clsx from 'clsx';
import React from 'react';

export interface ContainerQueryProps {
  children: React.ReactNode;
  containerSizes: number[];
  className?: string;
  style?: React.CSSProperties;
  isContainerQueryFallback?: boolean;
  onContainerSizeChange?: (number: number) => void;
}

const noop = () => {};

export function ContainerQuery(props: ContainerQueryProps) {
  const {
    children,
    style,
    className,
    containerSizes,
    isContainerQueryFallback = isSSRContainerQueryFallback,
    onContainerSizeChange,
  } = props;

  const ref = React.useRef<HTMLDivElement>(null);
  const [containerSizeClassName, setContainerSizeClassName] = React.useState('');

  const containerSizesSorted = React.useMemo(() => {
    return [...new Set(containerSizes)].sort((a, b) => b - a);
  }, [containerSizes]);

  useIsomorphicLayoutEffect(() => {
    if (!ref.current || !isContainerQueryFallback || !containerSizesSorted.length) {
      return noop;
    }
    const [, containerName] = (
      [...ref.current.classList].find((x) => x.startsWith('@container/')) || ''
    ).split('/');
    if (!containerName) {
      if (import.meta.env.DEV) {
        throw new Error('ContainerQuery: @container className is required');
      }
      return noop;
    }

    const observer = new ResizeObserver(([entry]) => {
      const contentRect = entry?.contentRect;
      if (contentRect) {
        const newClassName = containerSizesSorted
          .filter((x) => contentRect.width >= x)
          .map((x) => `${ContainerQueryFallback.prefix}${containerName}-${x}`)
          .join(' ');

        const fallbackSize = containerSizesSorted.filter((x) => contentRect.width >= x);
        onContainerSizeChange?.(fallbackSize[0] ?? 0);

        setContainerSizeClassName(newClassName);
      }
    });
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={ref} className={clsx(className, containerSizeClassName)} style={style}>
      {children}
    </div>
  );
}
